import React, { useState, useRef, useEffect } from "react";
import _ from "lodash";
import { Button, Box, Text } from "@showitinc/showit-wc";
import Editor, { loader } from "@monaco-editor/react";
import Modal from "../base/Modal";

import ButtonBar from "../base/ButtonBar";
import DesignViewStore from "../../stores/DesignViewStore";
import LinkWarning from "./LinkWarning";
import HTMLUtils from "../../utils/HTMLUtils";
import InputSanitizer from "../../utils/InputSanitizer";
import uiPrefix from "../../utils/UiPrefixUtils";
import useStoreState from "../providers/useStoreState";
import { COLOR_MODE_DARK, COLOR_MODE_LIGHT } from "../../constants/UIConstants";
import styles from "./HTMLEditorModal.module.css";
import ValidationErrorMessage from "./ValidationErrorMessage";
import {
  applyEditorFixes,
  suppressGlobalJSUndefinedErrors,
} from "@/utils/MonacoEditorFixes.mjs";

/**
 * @typedef {Parameters<import("@monaco-editor/react").OnMount>[0]} MonacoEditor = editor instance
 */

loader.init().then(suppressGlobalJSUndefinedErrors);

const HTMLEditorModal = ({
  validateHTML = true,
  includeSample = false,
  content,
  onSave,
  onHide,
  linkWarningEnabled,
  title,
  language = "html",
  ...otherProps
}) => {
  const [editedContent, setEditedContent] = useState(content);
  const [validationErrors, setValidationErrors] = useState([]);
  const editorRef = useRef(null);
  const colorMode = useStoreState(
    DesignViewStore,
    DesignViewStore.getColorMode,
  );

  const _cancel = () => {
    setEditedContent(content);
    setValidationErrors([]);
    onHide();
  };

  useEffect(() => {
    setEditedContent(content);
    setValidationErrors([]);
  }, [content]);

  const _save = () => {
    // Check if there are validation errors before saving
    if (validationErrors.length > 0) {
      return;
    }

    try {
      const result = {
        text: InputSanitizer.sanitize(editedContent),
      };

      if (validateHTML) {
        let opts = {};
        if (includeSample) {
          opts = { sample: 30 };
        }

        const validContent = HTMLUtils.formatToValidHTML(result?.text ?? "", opts);
        result.text = _.get(validContent, "text");
        if (_.has(validContent, "sample")) {
          result.sample = _.get(validContent, "sample");
        }
      }

      onSave(result);
    } catch (e) {
      console.error(e);
      setValidationErrors([{ message: "An error occurred while saving." }]);
    }
  };

  const _handleFormatClick = () => {
    if (editorRef.current) {
      const editor = editorRef.current;
      editor.getAction("editor.action.formatDocument").run();
    }
  };

  /**
   *
   * @param {MonacoEditor} editor
   */
  const _handleEditorDidMount = (editor) => {
    editorRef.current = editor;
    editor.focus();
    applyEditorFixes(editor);
  };

  const _onChange = (value) => {
    setEditedContent(value);
  };

  const _handleEditorValidation = (markers) => {
    setValidationErrors(markers);
  };

  const linkWarning = linkWarningEnabled ? <LinkWarning /> : null;

  const modalProps = _.pick(
    { onHide: _cancel, ...otherProps },
    Modal.BASIC_PROPS,
  );

  const errorMessageContent =
    validationErrors.length > 0
      ? `${validationErrors.length} error${
          validationErrors.length > 1 ? "s" : ""
        } found`
      : "code is invalid";

  const errorBadge = validationErrors.length > 0 && (
    <Box d="flex" align="center" px="3" py="1" borderRadius=".25rem" mr="3">
      <Text className={styles.error} variant="body-2">
        {errorMessageContent}
      </Text>
    </Box>
  );

  return (
    <Modal {...modalProps}>
      <Modal.Dialog size="large">
        <Modal.Dialog.Header closeButton>
          <Modal.Dialog.Title>
            <Box d="flex" align="center">
              <Text variant="body-1">{title} - </Text>
              <Box ml="3">
                <Button variant="outline" onClick={_handleFormatClick}>
                  Format
                </Button>
              </Box>
            </Box>
          </Modal.Dialog.Title>
        </Modal.Dialog.Header>
        <Modal.Dialog.Body>
          {linkWarning}
          <Editor
            defaultLanguage={language}
            defaultValue={content}
            theme={`vs-${
              colorMode === COLOR_MODE_LIGHT
                ? COLOR_MODE_LIGHT
                : COLOR_MODE_DARK
            }`}
            onChange={_onChange}
            onMount={_handleEditorDidMount}
            onValidate={_handleEditorValidation}
          />
        </Modal.Dialog.Body>
        <Modal.Dialog.Footer>
          <Box position="relative" width="100%">
            <ValidationErrorMessage validationErrors={validationErrors} />
            <ButtonBar>
              {errorBadge}
              <Box mr="3">
                <Button
                  variant="minimal"
                  data-e2e={uiPrefix("modal-cancel-btn")}
                  className={uiPrefix("modal-cancel-btn")}
                  onClick={_cancel}
                >
                  Cancel
                </Button>
              </Box>
              <Button
                variant="feature"
                data-e2e={uiPrefix("modal-save-btn")}
                className={uiPrefix("modal-save-btn")}
                onClick={_save}
                disabled={validationErrors.length > 0}
              >
                Save
              </Button>
            </ButtonBar>
          </Box>
        </Modal.Dialog.Footer>
      </Modal.Dialog>
    </Modal>
  );
};

export default HTMLEditorModal;
